"use client";
import { useState, useEffect } from "react";

const AGENTS = [
  {
    id: "a", label: "Agent A", num: "01",
    accent: "#049FD9", rgb: "4,159,217",
    breatheKf: "v2a-breathe-a", breatheDur: "8.6s",
    streamDur: "7.0s", pulseDelay: "0s", pulseDur: "5.0s",
    icon: (c: string) => (
      <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
        <circle cx="12" cy="12" r="8"   stroke={c} strokeWidth="1.3" strokeOpacity="0.5"/>
        <circle cx="12" cy="12" r="4.5" stroke={c} strokeWidth="1.3"/>
        <circle cx="12" cy="12" r="2"   fill={c}/>
        <line x1="12" y1="2"  x2="12" y2="5.5"  stroke={c} strokeWidth="1.1" strokeOpacity="0.4"/>
        <line x1="12" y1="18.5" x2="12" y2="22" stroke={c} strokeWidth="1.1" strokeOpacity="0.4"/>
        <line x1="2"  y1="12" x2="5.5" y2="12"  stroke={c} strokeWidth="1.1" strokeOpacity="0.4"/>
        <line x1="18.5" y1="12" x2="22" y2="12" stroke={c} strokeWidth="1.1" strokeOpacity="0.4"/>
      </svg>
    ),
  },
  {
    id: "b", label: "Agent B", num: "02",
    accent: "#22d3ee", rgb: "34,211,238",
    breatheKf: "v2a-breathe-b", breatheDur: "10.4s",
    streamDur: "8.5s", pulseDelay: "0.8s", pulseDur: "6.0s",
    icon: (c: string) => (
      <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
        <ellipse cx="12" cy="6.5" rx="6.5" ry="2.8" stroke={c} strokeWidth="1.3"/>
        <path d="M5.5 6.5v4.5c0 1.55 2.91 2.8 6.5 2.8s6.5-1.25 6.5-2.8V6.5" stroke={c} strokeWidth="1.3"/>
        <path d="M5.5 11v4.5c0 1.55 2.91 2.8 6.5 2.8s6.5-1.25 6.5-2.8V11" stroke={c} strokeWidth="1.3"/>
      </svg>
    ),
  },
  {
    id: "c", label: "Agent C", num: "03",
    accent: "#a78bfa", rgb: "167,139,250",
    breatheKf: "v2a-breathe-c", breatheDur: "9.2s",
    streamDur: "8.0s", pulseDelay: "1.6s", pulseDur: "6.5s",
    icon: (c: string) => (
      <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
        <path d="M12 2.5L14.4 8.6H21L15.6 12.5L17.8 19L12 15.5L6.2 19L8.4 12.5L3 8.6H9.6L12 2.5Z"
          stroke={c} strokeWidth="1.3" strokeLinejoin="round"/>
      </svg>
    ),
  },
  {
    id: "d", label: "Agent D", num: "04",
    accent: "#34d399", rgb: "52,211,153",
    breatheKf: "v2a-breathe-d", breatheDur: "11.2s",
    streamDur: "9.5s", pulseDelay: "2.4s", pulseDur: "5.5s",
    icon: (c: string) => (
      <svg viewBox="0 0 24 24" fill="none" width="22" height="22">
        <polyline points="2,15 6,9 10,13 14,7 18,11 22,5"
          stroke={c} strokeWidth="1.5" strokeLinejoin="round" strokeLinecap="round"/>
        <circle cx="6"  cy="9"  r="1.5" fill={c}/>
        <circle cx="14" cy="7"  r="1.5" fill={c}/>
        <circle cx="22" cy="5"  r="1.5" fill={c}/>
      </svg>
    ),
  },
] as const;

const DOTS = [[0, 3.5], [0.40, 3], [0.75, 3.5]] as const;

export function IsolatedAgentsV2() {
  return (
    <>
      <style>{`
        @keyframes v2a-breathe-a { 0%,100%{box-shadow:0 4px 18px rgba(4,159,217,0.14)}   50%{box-shadow:0 4px 30px rgba(4,159,217,0.36)} }
        @keyframes v2a-breathe-b { 0%,100%{box-shadow:0 4px 18px rgba(34,211,238,0.12)}  50%{box-shadow:0 4px 28px rgba(34,211,238,0.32)} }
        @keyframes v2a-breathe-c { 0%,100%{box-shadow:0 4px 18px rgba(167,139,250,0.16)} 50%{box-shadow:0 4px 30px rgba(167,139,250,0.40)} }
        @keyframes v2a-breathe-d { 0%,100%{box-shadow:0 4px 18px rgba(52,211,153,0.12)}  50%{box-shadow:0 4px 26px rgba(52,211,153,0.32)} }

        @keyframes v2a-ring {
          0%  { transform:scale(1);   opacity:0.30; }
          80% { transform:scale(2.0); opacity:0;    }
          100%{ transform:scale(2.0); opacity:0;    }
        }
        @keyframes v2a-dot {
          0%,100%{ opacity:0.35; } 50%{ opacity:1; }
        }
        @keyframes v2a-strip {
          0%,100%{ opacity:0.45; } 50%{ opacity:1; }
        }
        @keyframes v2a-stream {
          0%  { left:-8%;  opacity:0;    }
          10% { opacity:0.55; }
          90% { opacity:0.55; }
          100%{ left:108%; opacity:0;    }
        }
        @keyframes v2a-num {
          0%,100%{ opacity:0.45; } 50%{ opacity:0.80; }
        }
      `}</style>

      <div style={{
        width:"100%", height:"100%", minHeight:440,
        background:"linear-gradient(150deg,#050709 0%,#08090F 60%,#060810 100%)",
        fontFamily:"'Inter',system-ui,sans-serif",
        display:"flex", alignItems:"center", justifyContent:"center",
        padding:"14px 16px 14px 8px", boxSizing:"border-box",
        position:"relative", overflow:"hidden",
      }}>
        {/* bg radial */}
        <div style={{ position:"absolute",inset:0,pointerEvents:"none",
          background:"radial-gradient(ellipse 60% 50% at 50% 50%, rgba(4,159,217,0.05) 0%, rgba(167,139,250,0.04) 55%, transparent 80%)" }} />

        {/* faint grid */}
        <svg style={{ position:"absolute",inset:0,width:"100%",height:"100%",opacity:0.055,pointerEvents:"none" }}>
          {[80,175,270,365,460].map(y=><line key={y} x1="0" y1={y} x2="480" y2={y} stroke="#049FD9" strokeWidth="0.5"/>)}
          {[60,160,260,360,440].map(x=><line key={x} x1={x} y1="0" x2={x} y2="540" stroke="#049FD9" strokeWidth="0.5"/>)}
        </svg>

        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, width:"100%", maxWidth:440 }}>
          {AGENTS.map((A,i) => <V2Card key={A.id} agent={A} delay={i*110} />)}
        </div>
      </div>
    </>
  );
}

function V2Card({ agent:A, delay }: { agent:typeof AGENTS[number]; delay:number }) {
  const [vis, setVis] = useState(false);
  useEffect(() => { const id=setTimeout(()=>setVis(true), 150+delay); return ()=>clearTimeout(id); }, [delay]);

  return (
    <div style={{
      borderRadius:13, overflow:"hidden", display:"flex",
      border:`1px solid rgba(${A.rgb},0.22)`,
      background:`rgba(${A.rgb},0.04)`,
      position:"relative",
      opacity:vis?1:0,
      transform:vis?"translateY(0) scale(1)":"translateY(14px) scale(0.97)",
      transition:"opacity 0.45s cubic-bezier(0.22,1,0.36,1), transform 0.38s cubic-bezier(0.22,1,0.36,1)",
      animation:vis?`${A.breatheKf} ${A.breatheDur} ease-in-out infinite`:"none",
    }}>
      {/* ── Left accent strip ── */}
      <div style={{
        width:5, flexShrink:0,
        background:`linear-gradient(to bottom, ${A.accent}, rgba(${A.rgb},0.45))`,
        opacity:0.6,
        animation:`v2a-strip ${A.breatheDur} ease-in-out infinite`,
      }} />

      {/* ── Content ── */}
      <div style={{ flex:1, display:"flex", flexDirection:"column", position:"relative" }}>
        {/* inner glow */}
        <div style={{ position:"absolute",inset:0,pointerEvents:"none",
          background:`radial-gradient(ellipse 100% 60% at 0% 50%, rgba(${A.rgb},0.08) 0%, transparent 60%)` }} />

        {/* ── Top row: number + icon ── */}
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", padding:"12px 12px 8px", position:"relative" }}>
          {/* number */}
          <span style={{
            fontSize:9, fontWeight:700, fontFamily:"'SF Mono',monospace",
            color:A.accent, letterSpacing:"0.12em",
            animation:`v2a-num ${A.breatheDur} ease-in-out infinite`,
          }}>
            {A.num}
          </span>

          {/* icon with ring  -  zIndex so ring is visible above card */}
          <div style={{ position:"relative", width:38, height:38, flexShrink:0 }}>
            <div style={{
              position:"absolute", inset:-6, borderRadius:"50%",
              border:`1.5px solid rgba(${A.rgb},0.65)`,
              animation:`v2a-ring ${A.pulseDur} ease-out ${A.pulseDelay} infinite`,
            }} />
            <div style={{
              width:38, height:38, borderRadius:11,
              background:`rgba(${A.rgb},0.11)`,
              border:`1px solid rgba(${A.rgb},0.30)`,
              display:"flex", alignItems:"center", justifyContent:"center",
              color:A.accent, position:"relative", zIndex:1,
              boxShadow:`0 0 10px rgba(${A.rgb},0.18)`,
            }}>
              {A.icon(A.accent)}
            </div>
          </div>
        </div>

        {/* ── Name ── */}
        <div style={{ padding:"0 12px 7px" }}>
          <p style={{ margin:0, fontSize:13, fontWeight:700, color:"#fff", letterSpacing:"-0.015em", lineHeight:1.15 }}>
            {A.label}
          </p>
        </div>

        {/* ── Divider ── */}
        <div style={{ height:1, background:`rgba(${A.rgb},0.16)`, margin:"0 12px 8px" }} />

        {/* ── Status row ── */}
        <div style={{ display:"flex", alignItems:"center", gap:5, padding:"0 12px 10px" }}>
          <div style={{
            width:5, height:5, borderRadius:"50%",
            background:A.accent,
            boxShadow:`0 0 4px 1px rgba(${A.rgb},0.65)`,
            animation:`v2a-dot ${A.pulseDur} ease-in-out ${A.pulseDelay} infinite`,
          }} />
          <span style={{ fontSize:8.5, color:A.accent, fontWeight:700, letterSpacing:"0.08em", textTransform:"uppercase", opacity:0.75 }}>
            Active
          </span>
          <span style={{ fontSize:9, color:`rgba(255,255,255,0.20)`, marginLeft:"auto", letterSpacing:"0.04em" }}>
            Isolated
          </span>
        </div>

        {/* ── Stream track ── */}
        <div style={{
          margin:"0 12px 12px", height:12, borderRadius:999,
          background:`rgba(${A.rgb},0.055)`,
          border:`1px solid rgba(${A.rgb},0.14)`,
          position:"relative", overflow:"hidden",
        }}>
          <div style={{
            position:"absolute", top:"50%", left:0, right:0, height:1,
            background:`linear-gradient(to right, transparent, rgba(${A.rgb},0.22), transparent)`,
            transform:"translateY(-50%)",
          }} />
          {DOTS.map(([frac, sz], di) => (
            <div key={di} style={{
              position:"absolute", top:"50%", width:sz, height:sz,
              borderRadius:"50%", left:0,
              background:`radial-gradient(circle, ${A.accent} 30%, rgba(${A.rgb},0.4) 70%, transparent 100%)`,
              boxShadow:`0 0 3px 1px rgba(${A.rgb},0.22)`,
              transform:"translateY(-50%)",
              animation:`v2a-stream ${A.streamDur} linear calc(${frac} * ${A.streamDur}) infinite`,
            }} />
          ))}
        </div>
      </div>
    </div>
  );
}
